import React, { useState, useEffect } from 'react';
import { RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import PapersOverview from './PapersOverview';
import UsersOverview from './UsersOverview';
import ReviewsOverview from './ReviewsOverview';
import CommentsOverview from './CommentsOverview';
import RemediationOverview from './RemediationOverview';
import LicenseRemediationOverview from './LicenseRemediationOverview';
import SignupRiskOverview from './SignupRiskOverview';
import HeaderWithBackButton from '@/components/common/HeaderWithBackButton';
import ArxivBatchFetch from './ArxivBatchFetch';
import { useSearchParams } from 'react-router-dom';
import DashboardFilterBar from '@/components/admin/dashboard/DashboardFilterBar';
import OperationsOverview from '@/components/admin/dashboard/OperationsOverview';
import ContentTrends from '@/components/admin/dashboard/ContentTrends';
import { parseFilters, writeFilters, type DashboardFilters } from '@/lib/admin/dashboardFilters';
import { ArxivAutoImportConfig } from './arxiv/ArxivAutoImportConfig';
import { ArxivVersionBackfill } from './arxiv/ArxivVersionBackfill';

interface AdminContentDashboardProps {
  isAdmin: boolean;
}

export interface ContentStats {
  papers: {
    total: number;
    totalAutoImported: number;
    totalUserAdded: number;
    totalPrivate: number;
    withPdf: number;
    withExternalUrl: number;
    byCategory: Record<string, number>;
    byMonth: Record<string, number>;
    byMonthBySource: Record<string, { auto_imported: number; user_added: number; private: number }>;
  };
  users: {
    total: number;
    authors: number;
    byMonth: Record<string, number>;
  };
  reviews: {
    total: number;
    published: number;
    draft: number;
    paperDistribution: Record<string, number>;
    averagePerPaper: number;
  };
  comments: {
    total: number;
    replies: number;
    byPaper: Array<{ paperId: string; paperTitle: string; count: number }>;
    byMonth: Record<string, number>;
  };
}

const AdminContentDashboard: React.FC<AdminContentDashboardProps> = ({ isAdmin }) => {
  const { user } = useAuth();
  const [stats, setStats] = useState<ContentStats | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<string>('papers');
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = parseFilters(searchParams);
  const area = searchParams.get('area') === 'operations' ? 'operations' : 'content';
  const setFilters = (f: DashboardFilters) => setSearchParams(writeFilters(searchParams, f), { replace: true });
  const setArea = (a: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('area', a);
    setSearchParams(next, { replace: true });
  };
  const [reviewSplit, setReviewSplit] = useState<{ human: number; ai: number } | null>(null);

  const fetchContentStats = async () => {
    setLoading(true);
    try {
      const [{ data, error }, split] = await Promise.all([
        supabase.rpc('get_admin_content_stats'),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (supabase.rpc as any)('admin_review_split'),
      ]);
      
      if (error) {
        throw error;
      }
      
      setStats(data as ContentStats);
      if (!split.error && split.data) setReviewSplit(split.data as { human: number; ai: number });
      toast.success('Content statistics loaded successfully');
    } catch (error) {
      console.error('Error fetching content stats:', error);
      toast.error('Failed to load content statistics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin && user) {
      fetchContentStats();
    }
  }, [isAdmin, user]);

  return (
    <>
      <HeaderWithBackButton 
        title="Content Overview" 
        subtitle="Detailed statistics about platform content and engagement"
      />
      
      <div className="mb-8 flex justify-end">
        <Button onClick={fetchContentStats} disabled={loading} size="sm" className="gap-1">
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh Data
        </Button>
      </div>

      <Tabs value={area} onValueChange={setArea}>
        <TabsList className="mb-4">
          <TabsTrigger value="content">Content</TabsTrigger>
          <TabsTrigger value="operations">Operations</TabsTrigger>
        </TabsList>
        <DashboardFilterBar filters={filters} onChange={setFilters} showVisitorFilters={area === 'operations'} />

        <TabsContent value="operations" className="animate-fade-in space-y-8">
          <OperationsOverview filters={filters} />
          <SignupRiskOverview />
        </TabsContent>

        <TabsContent value="content" className="animate-fade-in">
      <ContentTrends filters={filters} />
      <p className="mb-3 text-sm text-muted-foreground">All-time totals</p>
      {/* Platform Metrics Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <Card>
          <CardHeader className="py-4 px-6">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Papers
            </CardTitle>
          </CardHeader>
          <CardContent className="py-0 px-6">
            {loading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <p className="text-3xl font-bold">{stats?.papers.total || 0}</p>
            )}
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="py-4 px-6">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Users
            </CardTitle>
          </CardHeader>
          <CardContent className="py-0 px-6">
            {loading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <p className="text-3xl font-bold">{stats?.users.total || 0}</p>
            )}
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="py-4 px-6">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Human Reviews
            </CardTitle>
          </CardHeader>
          <CardContent className="py-0 px-6">
            {loading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <>
                <p className="text-3xl font-bold">{reviewSplit?.human ?? 0}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  plus {reviewSplit?.ai ?? 0} AI reviews
                </p>
              </>
            )}
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="py-4 px-6">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Comments
            </CardTitle>
          </CardHeader>
          <CardContent className="py-0 px-6">
            {loading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <p className="text-3xl font-bold">
                {(stats?.comments.total || 0) + (stats?.comments.replies || 0)}
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Detailed Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="papers">Papers</TabsTrigger>
          <TabsTrigger value="users">Users</TabsTrigger>
          <TabsTrigger value="reviews">Reviews</TabsTrigger>
          <TabsTrigger value="comments">Comments</TabsTrigger>
          <TabsTrigger value="remediation">Remediation</TabsTrigger>
          <TabsTrigger value="license">License</TabsTrigger>
          <TabsTrigger value="arxiv">arXiv Fetch</TabsTrigger>
        </TabsList>
        
        <TabsContent value="papers" className="animate-fade-in">
          <PapersOverview loading={loading} stats={stats?.papers} />
        </TabsContent>
        
        <TabsContent value="users" className="animate-fade-in">
          <UsersOverview loading={loading} stats={stats?.users} />
        </TabsContent>
        
        <TabsContent value="reviews" className="animate-fade-in">
          <ReviewsOverview loading={loading} stats={stats?.reviews} />
        </TabsContent>
        
        <TabsContent value="comments" className="animate-fade-in">
          <CommentsOverview loading={loading} stats={stats?.comments} />
        </TabsContent>
        
        <TabsContent value="remediation" className="animate-fade-in">
          <RemediationOverview />
        </TabsContent>
        
        <TabsContent value="license" className="animate-fade-in">
          <LicenseRemediationOverview />
        </TabsContent>

        
        <TabsContent value="arxiv" className="animate-fade-in">
          <div className="space-y-6">
            <ArxivAutoImportConfig />
            <ArxivVersionBackfill />
            <ArxivBatchFetch />
          </div>
        </TabsContent>
      </Tabs>
        </TabsContent>
      </Tabs>
    </>
  );
};

export default AdminContentDashboard;
